import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import {
  fetchSecurityQuestions,
  fetchUsrSecurityQuestions,
  createUsrSecurityQuestions,
  patchUsrSecurityQuestions,
} from './SecurityApi';

const PSecurityQuestionModal = ({ show, onHide }) => {
  const [allQuestions, setAllQuestions] = useState([]);
  const [userQuestions, setUserQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState(
    Array(5).fill({ question_id: '', answer: '' })
  );
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState({ question_id: '', answer: '' });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSetupStarted, setIsSetupStarted] = useState(false);

  useEffect(() => {
    fetchSecurityQuestions().then(setAllQuestions);
    fetchUsrSecurityQuestions().then(setUserQuestions);
  }, []);

  const handleDropdownChange = (index, field, value) => {
    const updated = [...selectedQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedQuestions(updated);
  };

  const handleSave = async () => {
    if (isSaving) return;
    setFormError('');

    // For setup: validate all questions + answers
    if (isSetupStarted) {
      const isValid = selectedQuestions.every(
        (q) => q.question_id && q.answer.trim() !== ''
      );
      const uniqueQuestionIds = new Set(selectedQuestions.map((q) => q.question_id));

      if (!isValid) {
        setFormError('Please complete all questions and answers.');
        return;
      }
      if (uniqueQuestionIds.size < selectedQuestions.length) {
        setFormError('Each question must be unique.');
        return;
      }
    } else if (editingIndex !== null) {
      // For editing answer only
      if (!editValue.answer.trim()) {
        setFormError('Answer cannot be empty.');
        return;
      }
    }

    setIsSaving(true);
    try {
      if (isSetupStarted) {
        await createUsrSecurityQuestions(
          selectedQuestions.map((q) => ({
            question_id: q.question_id,
            answer_hash: q.answer.trim(),
          }))
        );
        setIsSetupStarted(false);
        fetchUsrSecurityQuestions().then(setUserQuestions);
        onHide();
      } else if (editingIndex !== null) {
        const payload = {
          id: userQuestions[editingIndex].id,
          question_id: editValue.question_id,
          answer_hash: editValue.answer.trim(),
        };
        await patchUsrSecurityQuestions(payload);
        setEditingIndex(null);
        fetchUsrSecurityQuestions().then(setUserQuestions);
      }
      setFormError('');
    } catch (err) {
      console.error('Error saving questions:', err);
      setFormError('Failed to save. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        setIsSetupStarted(false);
        setEditingIndex(null);
        setFormError('');
        onHide();
      }}
      centered
      scrollable
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Security Questions</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h5 className="mb-4 text-center">Security Questions</h5>
        {formError && <Alert variant="danger">{formError}</Alert>}

        {userQuestions.length === 0 ? (
          <>
            {!isSetupStarted ? (
              <div className="text-center">
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    setSelectedQuestions(Array(5).fill({ question_id: '', answer: '' }));
                    setIsSetupStarted(true);
                  }}
                >
                  Setup Security Questions
                </Button>
              </div>
            ) : (
              <>
                {selectedQuestions.map((q, idx) => (
                  <div className="mb-4" key={idx}>
                    <Form.Group>
                      <Form.Label>Question {idx + 1}</Form.Label>
                      <Form.Select
                        value={q.question_id}
                        onChange={(e) =>
                          handleDropdownChange(idx, 'question_id', e.target.value)
                        }
                      >
                        <option value="">-- Select a question --</option>
                        {allQuestions.map((qItem) => (
                          <option
                            key={qItem.id}
                            value={qItem.id}
                            disabled={selectedQuestions.some(
                              (sq, i) => i !== idx && sq.question_id === qItem.id
                            )}
                          >
                            {qItem.question_text}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mt-2">
                      <Form.Control
                        type="text"
                        placeholder="Enter your answer"
                        value={q.answer}
                        onChange={(e) =>
                          handleDropdownChange(idx, 'answer', e.target.value)
                        }
                      />
                    </Form.Group>
                  </div>
                ))}
              </>
            )}
          </>
        ) : (
          <>
            <ul className="list-group mb-3">
              {userQuestions.map((uq, index) => (
                <li
                  key={uq.id}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  {editingIndex === index ? (
                    <div className="w-100">
                      <Form.Group className="mb-2">
                        <Form.Label>Question</Form.Label>
                        <Form.Select
                          value={editValue.question_id}
                          onChange={(e) =>
                            setEditValue({ ...editValue, question_id: e.target.value })
                          }
                        >
                          <option value="">-- Select a question --</option>
                          {allQuestions.map((qItem) => (
                            <option
                              key={qItem.id}
                              value={qItem.id}
                              disabled={userQuestions.some(
                                (sq, i) =>
                                  i !== editingIndex && sq.question_id === qItem.id
                              )}
                            >
                              {qItem.question_text}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group>
                        <Form.Label>Answer</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter your answer"
                          value={editValue.answer}
                          onChange={(e) =>
                            setEditValue({ ...editValue, answer: e.target.value })
                          }
                        />
                      </Form.Group>

                      <div>
                        <Button
                          size="sm"
                          variant="success"
                          className="me-2"
                          disabled={isSaving || isUpdating}
                          onClick={handleSave}
                        >
                          {isSaving || isUpdating ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setEditingIndex(null);
                            setFormError('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span>
                        {index + 1}. {uq.question_text}
                      </span>
                      <Button
                        size="sm"
                        variant="link"
                        onClick={() => {
                          setEditingIndex(index);
                          setEditValue({
                            question_id: uq.question_id,
                            answer: uq.answer || '', // prefill with the existing answer
                          });
                          setFormError('');
                        }}
                      >
                        Edit Answer
                      </Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={() => {
            setIsSetupStarted(false);
            setEditingIndex(null);
            setFormError('');
            onHide();
          }}
          disabled={isSaving || isUpdating}
        >
          Cancel
        </Button>

        {/* Save button shows if in setup mode or editing an answer */}
        {(isSetupStarted || editingIndex !== null) && (
          <Button variant="primary" onClick={handleSave} disabled={isSaving || isUpdating}>
            {(isSaving || isUpdating) ? 'Saving...' : 'Save'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const PSecurityQuestionModalWrapper = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        className="btn btn-outline-secondary"
        onClick={() => setShowModal(true)}
      >
        Security Questions
      </button>

      <PSecurityQuestionModal
        show={showModal}
        onHide={() => setShowModal(false)}
      />
    </>
  );
};

export default PSecurityQuestionModalWrapper;
